import { useState, useEffect, useRef, useMemo } from 'react'
import { useFormContext, FieldValues, Path, PathValue } from 'react-hook-form'
import { z } from 'zod'
import { ReactComponent as ArrowDownIcon } from '@svgs/keyline/arrow-down.svg'
import classNames from 'classnames'
import styles from './styles.module.scss'
import { FieldErrorMessage } from '../shared/ErrorMessage'
import SelectDropdown from './Dropdown'
import labelStyles from '../shared/fieldLabel.module.scss'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'

export type Option = {
  value: string
  label: string
  available?: boolean
  icon?: React.ReactNode
}

type SelectFieldProps<T extends FieldValues> = {
  id: Path<T>
  name: string
  options: Option[]
  required?: boolean
  disabled?: boolean
  placeholder?: string
  defaultValue?: string
  forceDirection?: 'up' | 'down'
  settings?: {
    label?: {
      visible: boolean
    }
  }
}

export function SelectField<T extends FieldValues>({
  id,
  name,
  options,
  required = false,
  disabled = false,
  placeholder = 'Please select',
  forceDirection,
  defaultValue,
  settings
}: SelectFieldProps<T>) {
  const {
    register,
    setValue,
    watch,
    formState: { errors }
  } = useFormContext<T>()

  const isMobile = useMediaQuery({
    maxWidth: BREAKPOINT_SIZES.DESKTOP,
    initialState: true
  })

  const [isOpen, setIsOpen] = useState(false)
  const [direction, setDirection] = useState<'up' | 'down'>('down')

  const dropdownRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const selectedValue = watch(id) ?? defaultValue ?? ''

  const hasValue =
    selectedValue !== undefined &&
    selectedValue !== null &&
    (typeof selectedValue === 'number' ||
      String(selectedValue).trim().length > 0)

  const selectedOption = useMemo(
    () => options.find(o => o.value === selectedValue),
    [options, selectedValue]
  )

  const labelClasses = classNames(labelStyles.FieldLabel__name, {
    [labelStyles.disabled]: disabled,
    [labelStyles.focused]: isOpen,
    [labelStyles.required]: required
  })

  useEffect(() => {
    if (!isOpen) return
    if (forceDirection) {
      setDirection(forceDirection)
      return
    }

    const triggerRect = triggerRef.current?.getBoundingClientRect()
    const dropdownHeight = dropdownRef.current?.offsetHeight ?? 200
    if (triggerRect) {
      const fitsBelow = triggerRect.bottom + dropdownHeight < window.innerHeight
      setDirection(fitsBelow ? 'down' : 'up')
    }
  }, [isOpen, forceDirection])

  const handleSelect = (value: PathValue<T, Path<T>>) => {
    setValue(id, value, { shouldValidate: true })
    setIsOpen(false)
  }

  const handleClickOutside = () => setIsOpen(false)

  useEffect(() => {
    // register field in RHF so it exists in form values & errors
    register(id)
  }, [id, register])

  return (
    <div className={styles.SelectWrapper} ref={dropdownRef}>
      {settings?.label?.visible && (
        <label
          htmlFor={id}
          id={`${id}-label`}
          className={classNames(labelStyles.FieldLabel, {
            [labelStyles.focused]: isOpen
          })}
        >
          <span className={labelClasses}>{name}</span>
        </label>
      )}

      {isMobile ? (
        <select
          {...register(id)}
          id={id}
          disabled={disabled}
          value={selectedValue || ''}
          className={styles.NativeSelect}
          data-testid={`select-field-${id}`}
          onChange={e => {
            const v = e.target.value as PathValue<T, Path<T>>
            setValue(id, v, { shouldValidate: true })
          }}
        >
          <option value="">{`${placeholder}${
            !settings?.label?.visible && required ? '*' : ''
          }`}</option>
          {options.map(opt => (
            <option
              key={opt.value}
              value={opt.value}
              disabled={!opt.available}
              data-testid={`list-option-${opt.value}`}
            >
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <div
          className={classNames(styles.CustomSelect, {
            [styles.disabled]: disabled,
            [styles.error]: errors[id] && !disabled
          })}
        >
          <button
            ref={triggerRef}
            id={id}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={`${id}-listbox`}
            aria-label={selectedOption?.label || placeholder}
            type="button"
            onClick={() => !disabled && setIsOpen(p => !p)}
            className={styles.SelectTrigger}
            data-testid={`select-field-${id}`}
          >
            <span
              className={classNames(styles.SelectTrigger__content, {
                [styles.required]: !settings?.label?.visible && required
              })}
            >
              {selectedOption?.icon && (
                <span className={styles.TriggerIcon}>
                  {selectedOption.icon}
                </span>
              )}
              <span className={`${!hasValue ? styles.defaultOption : ''}`}>
                {selectedOption?.label ||
                  `${placeholder}${
                    !settings?.label?.visible && required ? '*' : ''
                  }`}
              </span>
            </span>
            <ArrowDownIcon
              className={classNames(styles.Icon, {
                [styles.Icon__upwards]: direction === 'up'
              })}
            />
          </button>

          {isOpen && (
            <SelectDropdown
              direction={direction}
              options={options}
              handleSelect={handleSelect}
              handleClickOutside={handleClickOutside}
              selectedValue={selectedValue}
              hasLabel={settings?.label?.visible ?? true}
              id={id}
            />
          )}
        </div>
      )}

      {errors[id] && !disabled && (
        <FieldErrorMessage name={id} errors={errors} />
      )}
    </div>
  )
}

SelectField.getSchema = (required: boolean, options: Option[]) => {
  const values = options.map(o => o.value)
  return required
    ? z
        .string()
        .min(1, 'This field is required')
        .refine(val => values.includes(val), 'Invalid option')
    : z
        .string()
        .optional()
        .refine(val => !val || values.includes(val), 'Invalid option')
}
